import { useEffect, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "../../components/ui/sheet";
import {
  Check, Circle, CircleDot, CheckCircle2, CalendarClock, StickyNote,
  Sparkles, HeartHandshake, Send, Loader2, Wand2, ListChecks,
} from "lucide-react";
import client from "../../lib/api";

const STATUS = [
  { key: "not_started", label: "Not started", Icon: Circle },
  { key: "in_progress", label: "In progress", Icon: CircleDot },
  { key: "done", label: "Done", Icon: CheckCircle2 },
];

export function TaskDrawer({ open, onOpenChange, goalId, phase, onChanged }) {
  const [p, setP] = useState(phase);
  const [note, setNote] = useState(phase?.note || "");
  const [reflectText, setReflectText] = useState("");
  const [busy, setBusy] = useState("");
  const [supportNotice, setSupportNotice] = useState("");

  useEffect(() => {
    setP(phase);
    setNote(phase?.note || "");
    setReflectText("");
    setSupportNotice("");
  }, [phase?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!p) return null;
  const base = `/engagement/goal/${goalId}/roadmap/phase/${p.id}`;

  const apply = (updated) => { setP(updated); onChanged && onChanged(updated); };

  const setStatus = async (status) => {
    setBusy("status");
    try { const { data } = await client.patch(base, { status }); apply(data); }
    catch (_e) { /* silent */ } finally { setBusy(""); }
  };

  const toggleSub = async (st) => {
    try {
      const { data } = await client.post(`${base}/subtask/${st.id}`, { done: !st.done });
      apply(data);
    } catch (_e) { /* silent */ }
  };

  const saveReminder = async (val) => {
    try { const { data } = await client.patch(base, { reminder_on: val || null }); apply(data); }
    catch (_e) { /* silent */ }
  };

  const saveNote = async () => {
    if (note === (p.note || "")) return;
    try { const { data } = await client.patch(base, { note }); apply(data); }
    catch (_e) { /* silent */ }
  };

  const sendReflection = async () => {
    if (!reflectText.trim()) return;
    setBusy("reflect"); setSupportNotice("");
    try {
      const { data } = await client.post(`${base}/reflect`, { text: reflectText.trim() });
      const next = { ...p, reflections: [...(p.reflections || []), data.reflection] };
      apply(next);
      setReflectText("");
      if (data.support_notice) setSupportNotice(data.support_notice);
    } catch (_e) { /* silent */ } finally { setBusy(""); }
  };

  const suggestNext = async () => {
    setBusy("suggest");
    try {
      const { data } = await client.post(`${base}/suggest-next`);
      const next = { ...p, subtasks: [...(p.subtasks || []), data.subtask] };
      apply(next);
    } catch (_e) { /* silent */ } finally { setBusy(""); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto p-0"
        style={{ background: "hsl(var(--klareet-canvas))", borderLeft: "1px solid hsl(var(--klareet-line))" }}
        data-testid="task-drawer"
      >
        <div className="p-6 pb-24">
          <SheetHeader className="text-left space-y-1">
            <span className="klareet-mono !text-[11px] uppercase tracking-wider" style={{ color: "hsl(var(--klareet-muted))" }} data-testid="drawer-window">{p.window}</span>
            <SheetTitle asChild>
              <h2 className="klareet-serif" style={{ fontSize: "1.7rem", lineHeight: 1.2, color: "hsl(var(--klareet-ink))" }} data-testid="drawer-title">{p.title}</h2>
            </SheetTitle>
          </SheetHeader>

          {/* status control */}
          <div className="flex gap-2 mt-4" data-testid="drawer-status">
            {STATUS.map((s) => {
              const on = p.status === s.key;
              const Icon = s.Icon;
              return (
                <button
                  key={s.key}
                  onClick={() => setStatus(s.key)}
                  disabled={busy === "status"}
                  data-testid={`drawer-status-${s.key}`}
                  className="flex-1 px-3 py-2 rounded-full text-xs font-medium border transition-all duration-200 flex items-center justify-center gap-1.5"
                  style={{
                    background: on ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-canvas-2))",
                    color: on ? "#fff" : "hsl(var(--klareet-ink))",
                    borderColor: on ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line-soft))",
                  }}
                >
                  <Icon size={13} /> {s.label}
                </button>
              );
            })}
          </div>

          {/* why it matters */}
          <div className="mt-6">
            <div className="k-label mb-1.5">why this stop matters</div>
            <p className="k-quiet !text-sm" style={{ lineHeight: 1.6 }} data-testid="drawer-why">{p.detail}</p>
          </div>

          {/* checklist */}
          <div className="mt-6">
            <div className="k-label mb-2 flex items-center gap-1.5"><ListChecks size={13} /> your steps</div>
            <div className="space-y-2" data-testid="drawer-checklist">
              {(p.subtasks || []).map((st) => (
                <button
                  key={st.id}
                  onClick={() => toggleSub(st)}
                  data-testid={`drawer-subtask-${st.id}`}
                  className="w-full text-left flex items-start gap-2.5 rounded-xl p-3 transition-colors"
                  style={{
                    background: st.done ? "hsl(var(--klareet-teal) / 0.08)" : "hsl(var(--klareet-canvas-2))",
                    border: `1px solid ${st.done ? "hsl(var(--klareet-teal) / 0.28)" : "hsl(var(--klareet-line-soft))"}`,
                  }}
                >
                  <span className="w-5 h-5 rounded-md shrink-0 mt-0.5 flex items-center justify-center" style={{ background: st.done ? "hsl(var(--klareet-teal))" : "#fff", border: `1px solid ${st.done ? "hsl(var(--klareet-teal))" : "hsl(var(--klareet-line))"}` }}>
                    {st.done && <Check size={12} color="#fff" />}
                  </span>
                  <span className="text-sm flex-1" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.45, textDecoration: st.done ? "line-through" : "none", opacity: st.done ? 0.7 : 1 }}>
                    {st.text}
                    {st.from_klair && <span className="k-chip k-chip-teal !text-[9px] ml-1.5"><Sparkles size={9} /> from Klair</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* reminder + note */}
          <div className="mt-6 grid grid-cols-1 gap-4">
            <div>
              <div className="k-label mb-1.5 flex items-center gap-1.5"><CalendarClock size={13} /> remind me by</div>
              <input
                type="date"
                className="k-input klareet-mono !text-sm"
                value={p.reminder_on || ""}
                onChange={(e) => saveReminder(e.target.value)}
                data-testid="drawer-reminder"
              />
            </div>
            <div>
              <div className="k-label mb-1.5 flex items-center gap-1.5"><StickyNote size={13} /> a note to yourself</div>
              <textarea
                className="k-input klareet-serif"
                rows={2}
                maxLength={1000}
                placeholder="anything you want to remember about this stop"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onBlur={saveNote}
                data-testid="drawer-note"
              />
            </div>
          </div>

          {/* Klair accountability block */}
          <div
            className="mt-6 rounded-2xl p-5"
            style={{ background: "hsl(var(--klareet-teal) / 0.07)", border: "1px solid hsl(var(--klareet-teal) / 0.22)" }}
            data-testid="klair-accountability"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal))", color: "#fff" }}><HeartHandshake size={16} /></span>
              <div>
                <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>Klair has your back</div>
                <div className="k-quiet !text-[11px]">i keep you honest, gently</div>
              </div>
            </div>

            <div className="rounded-xl p-3 mb-3" style={{ background: "hsl(var(--klareet-canvas))", border: "1px solid hsl(var(--klareet-line-soft))" }}>
              <div className="k-label mb-1">your commitment</div>
              <p className="klareet-serif !text-sm" style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.5 }} data-testid="klair-commitment">{p.commitment}</p>
            </div>

            {/* reflections thread */}
            {(p.reflections || []).length > 0 && (
              <div className="space-y-2 mb-3" data-testid="klair-reflections">
                {(p.reflections || []).map((r, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="text-sm rounded-xl rounded-tr-sm px-3 py-2 ml-8" style={{ background: "hsl(var(--klareet-ink))", color: "#fff", lineHeight: 1.45 }}>{r.you}</div>
                    <div className="text-sm rounded-xl rounded-tl-sm px-3 py-2 mr-8 flex gap-2" style={{ background: "hsl(var(--klareet-canvas))", border: "1px solid hsl(var(--klareet-line-soft))", color: "hsl(var(--klareet-ink))", lineHeight: 1.45 }}>
                      <Sparkles size={13} className="shrink-0 mt-0.5" style={{ color: "hsl(var(--klareet-teal))" }} />
                      <span>{r.klair}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="k-quiet !text-xs mb-2" data-testid="klair-checkin-prompt">{p.checkin_prompt}</div>
            <textarea
              className="k-input klareet-serif"
              rows={2}
              maxLength={800}
              placeholder="tell Klair how it went"
              value={reflectText}
              onChange={(e) => setReflectText(e.target.value)}
              data-testid="klair-reflection-input"
            />
            {supportNotice && <div className="k-warm-card mt-2 p-3 !text-xs" data-testid="klair-support-notice">{supportNotice}</div>}
            <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
              <button
                onClick={suggestNext}
                disabled={busy === "suggest"}
                className="k-btn k-btn-ghost !text-xs"
                data-testid="klair-suggest-next"
              >
                {busy === "suggest" ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Suggest my next step
              </button>
              <button
                onClick={sendReflection}
                disabled={!reflectText.trim() || busy === "reflect"}
                className="k-btn k-btn-primary !text-sm"
                data-testid="klair-reflection-send"
              >
                {busy === "reflect" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Tell Klair
              </button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default TaskDrawer;
